export interface OrderBatch {
	customerOrderNumber: string;
	customerOrderItemNumber: string;
	supplierOrderNumber: string;
	supplierOrderItemNumber: string;
	supplierBatchReference: string;
	vendorBatchReference: string;
	batchReference: string;
	mill: string;
	packageSerialNumber: string;
	millOrderNumber: string;
	millOrderItemNumber: string;
	batchStatus: string;
	batchStatusDescription: string;
	batchStatusGroup: string;
	qtyBatchNet: number;
	qtyBatchGross: number;
	thickness: number;
	length: number;
	width: number;
	heatNumber: string;
	readyToShipDate: string;
	availableInStockDate: string;
	daysInStock: number;
	plannedShipmentDate: string;
	currentLocation: string;
	currentLocationDescription: string;
	supplierShipmentReference: string;
	supplierShipmentReferenceItem: string;
	dispatchAdviceNumber: string;
	dispatchAdviceDate: string;
	destination: string;
	destinationDescription: string;
	shipments: OrderBatchShipment[];
	sapBatchId?: string;
}

export type FieldOfOrderBatch = keyof OrderBatch
	| `shipments.${ keyof OrderBatch['shipments'][number] }`;

interface OrderBatchShipment {
	transportReference: string;
	transportMode: string;
	transportModeDescription: string;
}

export const orderBatchFields = <const> [
	'customerOrderNumber',
	'customerOrderItemNumber',
	'supplierOrderNumber',
	'supplierOrderItemNumber',
	'supplierBatchReference',
	'vendorBatchReference',
	'batchReference',
	'mill',
	'packageSerialNumber',
	'millOrderNumber',
	'millOrderItemNumber',
	'batchStatus',
	'batchStatusGroup',
	'qtyBatchNet',
	'qtyBatchGross',
	'thickness',
	'width',
	'length',
	'heatNumber',
	'readyToShipDate',
	'availableInStockDate',
	'daysInStock',
	'plannedShipmentDate',
	'currentLocation',
	'currentLocationDescription',
	'supplierShipmentReference',
	'supplierShipmentReferenceItem',
	'dispatchAdviceNumber',
	'dispatchAdviceDate',
	'destination',
	'destinationDescription'
];

export const orderBatchShipmentFields = <const> [
	'shipments.transportReference',
	'shipments.transportMode',
	'shipments.transportModeDescription'
];

export interface Batch {
	id: string;
	customerOrderNumber: string;
	customerOrderItemNumber: string;
	supplierOrderNumber: string;
	supplierOrderItemNumber: string;
	batchReference: string;
	qtyBatchNet: number;
	qtyBatchGross: number;
	thickness: number;
	width: number;
	length: number;
	statusGroup: string;
	statusGroupDescription: string;
	status: string;
	statusDescription: string;
	batchStatus?: string;
	currentLocation: string;
	currentLocationDescription: string;
	packageSerialNumber: string;
	heatNumber: string;
	mill: string;
	millDescription: string;
	readyToShipDate: string;
	availableInStockDate?: string;
	plannedShipmentDate: string;
	daysInStock: number;
	supplierShipmentReference: string;
	supplierShipmentReferenceItem: string;
	lastDispatchAdviceNumber: string;
	lastDispatchAdviceDate: string;
	destination: string;
	destinationDescription: string;
	millBatchReference: string;
	cdd: string;
	cddWeek?: string;
	soldToDescription: string;
	shipTo: string;
	shipToDescription: string;
	steelgrade: string;
	customerArticleReference: string;
	millOrderNumber: string;
	millOrderItemNumber: string;
	grossFirstBatchInStock: string;
	grossLastBatchInStock: string;
	steelgradeDescription: string;
	finalUse: string;
	finalUseDescription: string;
	batchStatusCategory: string;
	batchStatusCategoryDescription: string;
	shipments?: BatchShipment[] | undefined;
	millbatchReference: string;
	loadingProgramNumber: string;
	transportReference: string;
	coating: string;
	coatingDescription: string;
	endUser: string;
	endUserDescription: string;
	supplierOrderType: string;
	supplierOrderTypeDescription: string;
	colorTopSide: string;
	colorTopSideDescription: string;
	colorBottomSide: string;
	colorBottomSideDescription: string;
	deliveryMode: string;
	deliveryModeDescription: string;
	serviceLevel: string;
	serviceLevelDescription: string;
	shipToAddress: string;
	ecallMillBatchReference: string;
	rowCreationDate: Date;
	customerReference: string;
	callCreator: string;
	ecallStatus?: string;
	ecallShipToDescription?: string;
	callOffCreationTimestamp?: Date;
	requestedDeliveryDateFrom?: Date;
	requestedDeliveryDateTo?: Date;
	requestedDateForCallOffType?: string;
	supplierInvoiceNumber?: string;
	invoiceDate?: string;
	transportMode: string;
	transportModeDescription: string;
	stockAlert?: string;
	allowChangeOfShipto: number;
	shipmentReferenceDate?: Date;
	salesQuarter?: string;
	destinationByBoat?: string;
	productCategorySTU?: string;
	productCategorySTUDescription?: string;
	rddWeek?: string;
	dispatchAdviceDateN1?: Date;
	dispatchAdviceNumberN1?: string;
	transportModeN1?: string;
	transportModeN1Description?: string;
	transportReferenceN1?: string;
	dispatchAdviceDateN2?: Date;
	dispatchAdviceNumberN2?: string;
	transportModeN2?: string;
	transportModeN2Description?: string;
	transportReferenceN2?: string;
	choice?: string;
	choiceDescription?: string;
	orderItemStatusDescription?: string;
	productDescription?: string;
	sfcCCountry?: string;
	sfcCDescription?: string;
	producedThickness?: number;
	producedWidth?: number;
	producedLength?: number;
	serviceRequest?: string;
	serviceRequestDescription?: string;
	callOffUnloadingReference?: string;
	supplierCallOffItemNumber?: string;
	form?: string;
	formDescription?: string;
	supplierBatchReference?: string;
	isAllowChangeOfShipto?: number;
	callable?: string;
	carrierName?: string;
	carrierNameConcat?: string;
}

export interface AggregateBatch {

	qtyBatchGross: number;

	qtyBatchNet: number;

}

export interface BatchShipment {
	dispatchDate: string;
	dispatchAdviceNumber: string;
	dispatchAdviceDate: string;
	shipmentTransportReference: string;
	shipmentTransportMode: string;
	shipmentTransportModeDescription: string;
	transportReference: string;
	lineShipmentReferenceDate: string;
	shipmentDestinationDescription: string;
}

export interface ShipmentTracking {
	shipmentReferenceDate?: string;
	location: string;
	transportMode: string;
	transportModeDescription?: string;
	transportReference?: string;
	showIcon: boolean;
	dispatchAdviceNumber?: string;
}

export type FieldOfBatch = keyof Batch | keyof BatchShipment;

export const batchShipmentFields = <const> [
	'dispatchAdviceNumber',
	'dispatchAdviceDate',
	'shipmentTransportReference',
	'shipmentTransportMode',
	'shipmentDestinationDescription',
	'lineShipmentReferenceDate'
];

export const batchCardFields = <const> [
	'id',
	'customerOrderNumber',
	'customerOrderItemNumber',
	'supplierOrderNumber',
	'supplierOrderItemNumber',
	'supplierOrderType',
	'batchReference',
	'mill',
	'millDescription',
	'packageSerialNumber',
	'millBatchReference',
	'loadingProgramNumber',
	'status',
	'statusDescription',
	'statusGroup',
	'statusGroupDescription',
	'qtyBatchNet',
	'qtyBatchGross',
	'thickness',
	'width',
	'length',
	'heatNumber',
	'readyToShipDate',
	'daysInStock',
	'plannedShipmentDate',
	'currentLocation',
	'currentLocationDescription',
	'supplierShipmentReference',
	'supplierShipmentReferenceItem',
	'lastDispatchAdviceNumber',
	'lastDispatchAdviceDate',
	'destination',
	'destinationDescription',
	'customerArticleReference',
	'millOrderNumber',
	'millOrderItemNumber',
	'steelgradeDescription',
	'finalUse',
	'finalUseDescription',
	'steelgrade',
	'invoiceDate',
	'supplierInvoiceNumber',
	'stockAlert',
	'form',
	'producedLength',
	'carrierName',
	'callable'
];
